import React from "react";
import Avatar from "../../components/Avatar";
import { Trash2, Power, KeyRound, AtSign, RotateCcw } from "lucide-react";

const StatusBadge = ({ u }) => {
  if (u.employment_status === "former") return <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200" title={u.former_since ? `Since ${String(u.former_since).slice(0, 10)}` : ""}>Former</span>;
  if (u.active_status) return <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">Active</span>;
  return <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">Inactive</span>;
};

/**
 * Pure presentation table for the User Management page. Receives the filtered
 * users + capability flags + per-row callbacks; never owns user data.
 */
export default function UserTable({
  users, currentUserId,
  canEdit, canDelete, canResetPwd, canManageFormer,
  canEditUserEmail, canEditSelfEmail,
  selected, onToggleSelect, onToggleAll,
  onEdit, onEditEmail, onResetPwd, onDeactivate, onDelete, onReinstate,
}) {
  const selectable = users.filter((u) => u.user_id !== currentUserId && u.employment_status !== "former");
  const allOn = selectable.length > 0 && selectable.every((u) => selected.has(u.user_id));
  return (
    <div className="nv-card overflow-x-auto">
      <table className="w-full text-sm" data-testid="user-table">
        <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
          <tr>
            {canManageFormer && <th className="pl-4 pr-1 py-3 text-left">{selectable.length > 0 && <input type="checkbox" checked={allOn} onChange={() => onToggleAll(selectable)} aria-label="Select all" data-testid="user-select-all" />}</th>}
            <th className="px-5 py-3 text-left">Name</th>
            <th className="px-3 py-3 text-left">Email</th>
            <th className="px-3 py-3 text-left">Job Title</th>
            <th className="px-3 py-3 text-left">Access Role</th>
            <th className="px-3 py-3 text-left">Classification</th>
            <th className="px-3 py-3 text-left">Home</th>
            <th className="px-3 py-3 text-left">Gender</th>
            <th className="px-3 py-3 text-left">Status</th>
            <th className="px-3 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => {
            const isSelf = u.user_id === currentUserId;
            const isFormer = u.employment_status === "former";
            const homeLabel = (u.assigned_homes && u.assigned_homes.length > 1)
              ? u.assigned_homes.join(" + ")
              : (u.assigned_home || "—");
            const showEditEmail = (isSelf && canEditSelfEmail) || (!isSelf && canEditUserEmail);
            return (
              <tr key={u.user_id} className={`border-t border-slate-100 hover:bg-slate-50/60 ${selected.has(u.user_id) ? "bg-rose-50/40" : ""}`} data-testid={`user-row-${u.user_id}`}>
                {canManageFormer && <td className="pl-4 pr-1 py-3"><input type="checkbox" disabled={isSelf || isFormer} checked={selected.has(u.user_id)} onChange={() => onToggleSelect(u.user_id)} aria-label={`Select ${u.full_name}`} data-testid={`select-user-${u.user_id}`} /></td>}
                <td className="px-5 py-3"><div className="flex items-center gap-2.5"><Avatar name={u.full_name} gender={u.gender} src={u.profile_image} size="sm" /><span className={`font-medium ${isFormer ? "text-slate-500" : "text-slate-800"}`}>{u.full_name}</span></div></td>
                <td className="px-3 py-3 text-slate-600 text-xs">{u.email}</td>
                <td className="px-3 py-3 text-slate-600">{u.job_title || u.role_title || "—"}</td>
                <td className="px-3 py-3"><span className="inline-flex text-xs font-semibold rounded-full px-2 py-0.5 bg-sky-50 text-sky-700 border border-sky-200">{u.access_role}</span></td>
                <td className="px-3 py-3 text-slate-600">{u.staff_classification || "—"}</td>
                <td className="px-3 py-3 text-slate-600">{homeLabel}</td>
                <td className="px-3 py-3 text-slate-600">{u.gender || "—"}</td>
                <td className="px-3 py-3"><StatusBadge u={u} /></td>
                <td className="px-3 py-3 text-right">
                  <div className="flex justify-end gap-2">
                    {canEdit && !isFormer && <button onClick={() => onEdit(u)} className="text-xs font-semibold text-sky-700 hover:underline" data-testid={`edit-user-${u.user_id}`}>Edit</button>}
                    {showEditEmail && !isFormer && (
                      <button onClick={() => onEditEmail(u)} title="Change login email" className="p-1.5 rounded hover:bg-indigo-50 text-indigo-700" data-testid={`edit-email-${u.user_id}`}>
                        <AtSign size={14} />
                      </button>
                    )}
                    {canResetPwd && !isSelf && !isFormer && <button onClick={() => onResetPwd(u)} title="Send password reset email" className="p-1.5 rounded hover:bg-sky-50 text-sky-700" data-testid={`reset-pwd-${u.user_id}`}><KeyRound size={14} /></button>}
                    {canEdit && u.active_status && !isSelf && <button onClick={() => onDeactivate(u)} title="Deactivate" className="p-1.5 rounded hover:bg-amber-50 text-amber-600" data-testid={`deactivate-user-${u.user_id}`}><Power size={14} /></button>}
                    {canManageFormer && isFormer && <button onClick={() => onReinstate(u)} title="Reinstate" className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold text-emerald-700 hover:bg-emerald-50 border border-emerald-200" data-testid={`reinstate-user-${u.user_id}`}><RotateCcw size={13} /> Reinstate</button>}
                    {canDelete && !isSelf && <button onClick={() => onDelete(u)} title="Delete" className="p-1.5 rounded hover:bg-rose-50 text-rose-600" data-testid={`delete-user-${u.user_id}`}><Trash2 size={14} /></button>}
                  </div>
                </td>
              </tr>
            );
          })}
          {users.length === 0 && <tr><td colSpan={canManageFormer ? 10 : 9} className="px-5 py-10 text-center text-slate-500">No users found.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
